import { useEffect, useMemo, useState } from 'react';

import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ContextIssuesList } from './components/context-issues-list';
import { ContextPartiesHints, ContextPartiesList } from './components/context-parties-list';
import { Permission } from '../../../../../../../backend/src/shared/action-registry';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useSubmitMatterIssues } from '@/api/mutations/useSubmitMatterIssues';
import { useSyncMatterParties } from '@/api/mutations/useSyncMatterParties';
import { useSyncSubsetIssues } from '@/api/mutations/useSyncSubsetIssues';
import { useUpdateMatter } from '@/api/mutations/useUpdateMatter';
import { useGetDocsCount } from '@/api/queries/useGetDocs';
import { useGetMatterParties } from '@/api/queries/useGetMatterParties';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { useGetSubsetIssues } from '@/api/queries/useGetSubsetIssues';
import { QueryEntity } from '@/api/types';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { InfoCard } from '@/new/components/info-card';
import { MAX_ADDITIONAL_CONTEXT_LENGTH, type IssueFormItem } from '@/new/components/matter/issues-list';
import { LegacyContextDialog, hasLegacyContext } from '@/new/components/matter/legacy-context-dialog';
import { type PartyFormItem } from '@/new/components/matter/parties-list';
import {
  SettingsContent,
  SettingsRow,
  SettingsSaveButton,
  SettingsSaveStatus,
  SettingsSection,
  SettingsSectionFooter,
} from '@/new/components/settings-section';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Textarea } from '@/new/components/shadcn/textarea';
import { cn } from '@/new/lib/utils';

export function Issues() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const queryClient = useQueryClient();
  const { data: matter } = useGetMatterV2(matterId);

  const allDocumentsCaseId = matter?.allDocumentsCaseId ?? undefined;
  const { data: serverIssues = [] } = useGetSubsetIssues(matterId, allDocumentsCaseId);
  const { data: serverParties = [] } = useGetMatterParties(matterId);
  const { mutateAsync: updateMatter } = useUpdateMatter(matterId);
  const { mutateAsync: submitIssues, isLoading: isSubmittingIssues } = useSubmitMatterIssues(matterId);
  const syncIssues = useSyncSubsetIssues(matterId, allDocumentsCaseId);
  const syncParties = useSyncMatterParties(matterId);

  const { data: allDocsSubset } = useGetSubset(matterId, matter?.allDocumentsCaseId ?? undefined);
  const { data: allDocsCase } = useGetSubset(matterId, allDocsSubset?.id);

  const hasLegacy = hasLegacyContext(allDocsCase);

  // Local state for issues and parties
  const [localIssues, setLocalIssues] = useState<IssueFormItem[]>([]);
  const [localParties, setLocalParties] = useState<PartyFormItem[]>([]);
  const [issuesSeeded, setIssuesSeeded] = useState(false);
  const [partiesSeeded, setPartiesSeeded] = useState(false);

  // Seed local state from server data
  useEffect(() => {
    if (serverIssues.length > 0 && !issuesSeeded) {
      setLocalIssues(serverIssues.map((i) => ({ id: i.id, label: i.label, text: i.text })));
      setIssuesSeeded(true);
    }
  }, [serverIssues, issuesSeeded]);

  useEffect(() => {
    if (serverParties.length > 0 && !partiesSeeded) {
      setLocalParties(
        serverParties.map((p) => ({
          id: p.id,
          name: p.name,
          isClient: p.isClient,
          role: p.role ?? 'other',
          type: p.type === 'place' ? null : p.type,
        })),
      );
      setPartiesSeeded(true);
    }
  }, [serverParties, partiesSeeded]);

  const [additionalContext, setAdditionalContext] = useState('');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showLegacyContext, setShowLegacyContext] = useState(false);
  const [savingSection, setSavingSection] = useState<'issues' | 'parties' | 'context' | null>(null);

  const { data: processedDocsCount } = useGetDocsCount({
    matterId: matterId ?? '',
    subsetId: allDocsSubset?.id ?? '',
    pageSize: 1,
    filters: { processed: true },
  });
  const hasDocuments = (processedDocsCount?.totalCount ?? 0) > 0;
  const reprocessDisabled = localIssues.length === 0 || isSubmittingIssues || !hasDocuments;

  const issuesChanged = useMemo(
    () =>
      localIssues.length !== serverIssues.length ||
      localIssues.some(
        (li, i) =>
          li.id !== serverIssues[i]?.id || li.label !== serverIssues[i]?.label || li.text !== serverIssues[i]?.text,
      ),
    [localIssues, serverIssues],
  );

  const partiesChanged = useMemo(
    () =>
      localParties.length !== serverParties.length ||
      localParties.some(
        (lp, i) =>
          lp.id !== serverParties[i]?.id ||
          lp.name !== serverParties[i]?.name ||
          lp.isClient !== serverParties[i]?.isClient ||
          lp.role !== (serverParties[i]?.role ?? 'other') ||
          (lp.type ?? null) !== (serverParties[i]?.type ?? null),
      ),
    [localParties, serverParties],
  );

  const contextChanged = additionalContext !== (matter?.additionalContext ?? '');

  useEffect(() => {
    if (matter?.additionalContext != null) {
      setAdditionalContext(matter.additionalContext);
    }
  }, [matter?.additionalContext]);

  const handleSaveIssues = async () => {
    if (!allDocumentsCaseId) return;
    setSavingSection('issues');
    try {
      await syncIssues.mutateAsync(localIssues);
      // Refresh server state so the dirty check recomputes and picks up generated IDs.
      await queryClient.invalidateQueries([QueryEntity.SubsetIssues, { matterId, subsetId: allDocumentsCaseId }]);
      setIssuesSeeded(false);
      trackCustomEvent(TrackingEventsV2.MatterIssuesSave, {
        matterId,
        issueCount: localIssues.length,
        partyCount: localParties.length,
      });
      toast.success('Issues saved.');
    } catch {
      toast.error('Failed to save issues.');
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveParties = async () => {
    setSavingSection('parties');
    try {
      await syncParties.mutateAsync(localParties);
      await queryClient.invalidateQueries([QueryEntity.MatterParties, { matterId }]);
      setPartiesSeeded(false);
      trackCustomEvent(TrackingEventsV2.MatterIssuesSave, {
        matterId,
        issueCount: localIssues.length,
        partyCount: localParties.length,
      });
      toast.success('Parties saved.');
    } catch {
      toast.error('Failed to save parties.');
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveContext = async () => {
    setSavingSection('context');
    try {
      await updateMatter({ additionalContext });
      await queryClient.invalidateQueries([QueryEntity.Matter, { matterId }]);
      toast.success('Additional context saved.');
    } catch {
      toast.error('Failed to save additional context.');
    } finally {
      setSavingSection(null);
    }
  };

  const handleReprocess = async () => {
    if (!allDocumentsCaseId) return;
    try {
      // Save first, then reprocess
      await Promise.all([
        syncIssues.mutateAsync(localIssues),
        syncParties.mutateAsync(localParties),
        updateMatter({
          additionalContext,
        }),
      ]);
      await submitIssues();
      trackCustomEvent(TrackingEventsV2.MatterIssuesSubmit, {
        matterId,
        issueCount: localIssues.length,
        partyCount: localParties.length,
      });
      setShowSubmitModal(false);
      setPartiesSeeded(false);
      setIssuesSeeded(false);
      toast.success('Changes saved. Synopsis update started.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to submit issues.');
    }
  };

  return (
    <PermissionGate permission={Permission.MatterEdit} uiMode="disabled">
      <SettingsContent>
        <div className="space-y-8">
          <div className="space-y-3">
            {matter && allDocsSubset && !hasDocuments && (
              <InfoCard>Add documents before updating Matter context.</InfoCard>
            )}

            {hasLegacy && (
              <p className="flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-amber-600">
                <Icon className="lucide--info" />
                This matter has context in the legacy format.{' '}
                <button
                  type="button"
                  onClick={() => setShowLegacyContext(true)}
                  className="inline-flex items-center gap-1 font-medium text-amber-600 hover:text-amber-700"
                >
                  View old context <Icon className="lucide--arrow-up-right" />
                </button>
              </p>
            )}
          </div>

          <SettingsSection
            title="Matter Context"
            description="Issues, parties, and context help Wexler determine the relevance of facts extracted from your documents."
            className={cn(reprocessDisabled && '[&_[data-settings-section=card]]:shadow-none')}
          >
            <SettingsRow
              title="Reprocess relevance"
              description="After updating your context, you can reprocess the Matter to update the relevance and issues on facts, as well as the Matter summary."
              className={cn(reprocessDisabled && 'opacity-50')}
            >
              <Button variant="primary" disabled={reprocessDisabled} onClick={() => setShowSubmitModal(true)}>
                Reprocess
                <Icon className="lucide--arrow-right h-3.5 w-3.5" />
              </Button>
            </SettingsRow>
          </SettingsSection>
        </div>

        <SettingsSection
          title="Issues"
          description="Issues help Wexler determine relevant facts extracted from your documents."
          footer={
            <SettingsSectionFooter>
              <SettingsSaveStatus variant={issuesChanged ? 'unsaved' : 'saved'}>
                {issuesChanged ? 'You have unsaved changes' : 'All changes saved'}
              </SettingsSaveStatus>
              <SettingsSaveButton
                isLoading={savingSection === 'issues'}
                hasUnsavedChanges={issuesChanged}
                onClick={handleSaveIssues}
                disabled={!issuesChanged || !hasDocuments || savingSection !== null}
                disabledReason={!hasDocuments ? 'Add documents before updating Matter context.' : undefined}
              />
            </SettingsSectionFooter>
          }
        >
          <ContextIssuesList issues={localIssues} onChange={setLocalIssues} />
        </SettingsSection>

        <div className="space-y-3">
          <SettingsSection
            title="Parties"
            description="Parties help Wexler identify relevant individuals and organisations across your documents."
            footer={
              <SettingsSectionFooter>
                <SettingsSaveStatus variant={partiesChanged ? 'unsaved' : 'saved'}>
                  {partiesChanged ? 'You have unsaved changes' : 'All changes saved'}
                </SettingsSaveStatus>
                <SettingsSaveButton
                  isLoading={savingSection === 'parties'}
                  hasUnsavedChanges={partiesChanged}
                  onClick={handleSaveParties}
                  disabled={!partiesChanged || !hasDocuments || savingSection !== null}
                  disabledReason={!hasDocuments ? 'Add documents before updating Matter context.' : undefined}
                />
              </SettingsSectionFooter>
            }
          >
            <ContextPartiesList parties={localParties} onChange={setLocalParties} />
          </SettingsSection>
          <ContextPartiesHints parties={localParties} />
        </div>

        <SettingsSection
          title="Additional Context"
          description="Any additional information about your matter that may help Wexler determine the relevance of facts."
          footer={
            <SettingsSectionFooter className="border-t">
              <SettingsSaveStatus variant={contextChanged ? 'unsaved' : 'saved'}>
                {contextChanged ? 'You have unsaved changes' : 'All changes saved'}
              </SettingsSaveStatus>
              <SettingsSaveButton
                isLoading={savingSection === 'context'}
                hasUnsavedChanges={contextChanged}
                onClick={handleSaveContext}
                disabled={!contextChanged || !hasDocuments || savingSection !== null}
                disabledReason={!hasDocuments ? 'Add documents before updating Matter context.' : undefined}
              />
            </SettingsSectionFooter>
          }
        >
          <Textarea
            className="min-h-44 resize-none border-0 bg-transparent px-5 py-4 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            value={additionalContext}
            onChange={(e) => setAdditionalContext(e.target.value)}
            placeholder="Add any additional context for this matter..."
            maxLength={MAX_ADDITIONAL_CONTEXT_LENGTH}
          />
        </SettingsSection>
      </SettingsContent>

      <Dialog open={showSubmitModal} onOpenChange={setShowSubmitModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reprocess Issues & Context</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p>
              This will save your changes and re-tag the relevance of all facts and generate a new summary based on this
              new information.
            </p>
            <p className="text-xs text-gray-500">
              This process may take some time depending on the number of facts in your matter.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleReprocess} disabled={isSubmittingIssues}>
              {isSubmittingIssues ? (
                <>
                  Processing...
                  <Icon className="lucide--loader-circle h-3.5 w-3.5 animate-spin duration-700" />
                </>
              ) : (
                <>
                  Confirm
                  <Icon className="lucide--arrow-right h-3.5 w-3.5" />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <LegacyContextDialog
        open={showLegacyContext}
        onOpenChange={setShowLegacyContext}
        title="Previous Matter Context"
        data={allDocsCase}
      />
    </PermissionGate>
  );
}
